import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Traslado } from '@/api/tienda.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import { fecha } from '@/lib/formato-tienda';

/**
 * LA TIENDA CONFIRMA LO QUE LE LLEGÓ.
 *
 * Lo que sale del almacén hacia una tienda queda «por confirmar» hasta que alguien de esa tienda
 * pone su nombre. Es la pregunta de Esteban («¿te las llevó? Sí») contestada en el panel: nadie
 * le escribe a nadie, se ve al abrir «Almacén y tiendas».
 */
export function ConfirmarTraslado({ traslado: t }: { traslado: Traslado }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pidiendo, setPidiendo] = useState(false);
  const [nombre, setNombre] = useState('');

  const confirmar = useMutation({
    mutationFn: () => tienda.confirmarTraslado(t.id, nombre.trim()),
    onSuccess: () => {
      toast.success(`Confirmado: llegó a ${t.destino?.nombre ?? 'la tienda'}.`);
      setPidiendo(false);
      void qc.invalidateQueries({ queryKey: ['inventario', 'traslados'] });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'No se pudo confirmar');
      void qc.invalidateQueries({ queryKey: ['inventario', 'traslados'] });
    },
  });

  if (t.recibidoAt) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
        <Check className="h-3.5 w-3.5" /> lo recibió {t.recibidoPor} · {fecha(t.recibidoAt)}
      </span>
    );
  }
  if (!pidiendo) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          por confirmar en {t.destino?.nombre}
        </span>
        <Button size="sm" variant="outline" onClick={() => setPidiendo(true)}>
          Confirmar que llegó
        </Button>
      </span>
    );
  }
  return (
    <form
      className="flex w-full flex-wrap items-center gap-2 sm:w-auto"
      onSubmit={(e) => {
        e.preventDefault();
        if (nombre.trim()) confirmar.mutate();
      }}
    >
      <div className="min-w-[10rem] flex-1">
        <Input
          autoFocus
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Quién lo recibió en la tienda"
          aria-label={`Quién recibió en ${t.destino?.nombre ?? 'la tienda'}`}
        />
      </div>
      <Button size="sm" type="submit" disabled={!nombre.trim() || confirmar.isPending} loading={confirmar.isPending}>
        Llegó
      </Button>
      <Button size="sm" type="button" variant="ghost" onClick={() => setPidiendo(false)}>
        Cancelar
      </Button>
    </form>
  );
}
